import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Download, FileCsv, ChartBar, UsersThree } from "@phosphor-icons/react";
import { priorityColor } from "@/lib/utils";
import { toast } from "sonner";
import { useChartColors } from "@/lib/useChartColors";
import { DatePicker } from "@/components/DatePicker";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ClientReport from "@/pages/ClientReport";
import { useSearchParams } from "react-router-dom";

const STATUSES = ["open", "in_progress", "on_hold", "resolved", "closed"];
const PRIORITIES = ["P1", "P2", "P3", "P4"];
const CHANGE_STATUSES = ["draft", "in_review", "approved", "rejected", "scheduled", "implemented", "closed"];
const PROBLEM_STATUSES = ["open", "investigating", "known_error", "closed"];

const DATASETS = {
  incidents: { label: "Incidents", endpoint: "/reports/custom", groupBys: ["status", "priority", "category", "assignee_name", "group", "source"], statuses: STATUSES },
  changes: { label: "Changes", endpoint: "/reports/changes", groupBys: ["status", "priority", "risk", "impact"], statuses: CHANGE_STATUSES },
  problems: { label: "Problems", endpoint: "/reports/problems", groupBys: ["status"], statuses: PROBLEM_STATUSES },
};

const emptyFilters = { status: "all", priority: "all", category: "all", assignee_id: "all", manager_id: "all", group_by: "status", date_from: "", date_to: "" };

const hhmm = (startIso, endIso) => {
  if (!startIso || !endIso) return "—";
  const secs = (new Date(endIso) - new Date(startIso)) / 1000;
  if (isNaN(secs) || secs < 0) return "—";
  const m = Math.floor(secs / 60);
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};

// Red when the actual response/resolution time missed its SLA target.
const slaBreachClass = (r, kind) => {
  const due = kind === "resp" ? r.sla_response_due_at : r.sla_resolution_due_at;
  const done = kind === "resp" ? r.responded_at : r.resolved_at;
  if (due && done && new Date(done) > new Date(due)) return "text-red-600 dark:text-red-400 font-semibold";
  return "";
};

export default function ReportBuilder({ embedded = false }) {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initDs = DATASETS[searchParams.get("ds")] ? searchParams.get("ds") : "incidents";
  const [dataset, setDataset] = useState(initDs);
  const tab = searchParams.get("sub") === "client" ? "client" : "builder";
  const setTab = (v) => {
    const p = new URLSearchParams(searchParams);
    if (v === "client") p.set("sub", "client"); else p.delete("sub");
    setSearchParams(p, { replace: true });
  };
  const filtersFromUrl = () => ({
    status: searchParams.get("bstatus") || "all",
    priority: searchParams.get("bpri") || "all",
    category: searchParams.get("bcat") || "all",
    assignee_id: searchParams.get("basg") || "all",
    manager_id: searchParams.get("bmgr") || "all",
    group_by: searchParams.get("bgb") || "status",
    date_from: searchParams.get("bfrom") || "",
    date_to: searchParams.get("bto") || "",
  });
  const [filters, setFilters] = useState(filtersFromUrl());
  const [agents, setAgents] = useState([]);
  const [managers, setManagers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const chart = useChartColors();
  const cfg = DATASETS[dataset];

  useEffect(() => {
    api.get("/users").then(r => {
      setAgents(r.data.filter(u => u.role === "agent" || u.role === "admin"));
      setManagers(r.data.filter(u => u.role === "manager" || u.role === "admin"));
    }).catch(() => {});
    api.get("/categories").then(r => setCategories(r.data)).catch(() => {});
    run(dataset, filters); // eslint-disable-next-line
  }, []);

  const syncUrl = (ds, f) => {
    const p = new URLSearchParams(searchParams);
    if (ds && ds !== "incidents") p.set("ds", ds); else p.delete("ds");
    const setOrDel = (k, v, def) => { if (v && v !== def) p.set(k, v); else p.delete(k); };
    setOrDel("bstatus", f.status, "all");
    setOrDel("bpri", f.priority, "all");
    setOrDel("bcat", f.category, "all");
    setOrDel("basg", f.assignee_id, "all");
    setOrDel("bmgr", f.manager_id, "all");
    setOrDel("bgb", f.group_by, "status");
    setOrDel("bfrom", f.date_from, "");
    setOrDel("bto", f.date_to, "");
    setSearchParams(p, { replace: true });
  };

  const switchDataset = (ds) => {
    const next = { ...emptyFilters, group_by: DATASETS[ds].groupBys[0] };
    setDataset(ds);
    setFilters(next);
    setResult(null);
    run(ds, next);
  };

  const buildBody = (ds, f) => {
    const body = { group_by: f.group_by };
    if (f.status !== "all") body.status = f.status;
    if (ds === "incidents") {
      ["priority", "category", "assignee_id", "manager_id"].forEach(k => { if (f[k] !== "all") body[k] = f[k]; });
    }
    if (f.date_from) body.date_from = f.date_from;
    if (f.date_to) body.date_to = f.date_to;
    return body;
  };

  const run = async (ds = dataset, f = filters) => {
    setLoading(true);
    syncUrl(ds, f);
    try {
      const { data } = await api.post(DATASETS[ds].endpoint, buildBody(ds, f));
      setResult({ ...data, _dataset: ds });
    } catch (e) {
      toast.error("Failed to run report");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { const next = { ...emptyFilters, group_by: cfg.groupBys[0] }; setFilters(next); run(dataset, next); };

  const kpis = () => {
    if (!result) return [];
    if (result._dataset === "changes") return [
      { label: "Total", value: result.total, color: "text-primary" },
      { label: "Implemented", value: result.implemented, color: "text-emerald-700 dark:text-emerald-400" },
      { label: "Rejected", value: result.rejected, color: "text-red-700 dark:text-red-400" },
      { label: "Pending", value: result.pending, color: "text-amber-700 dark:text-amber-400" },
    ];
    if (result._dataset === "problems") return [
      { label: "Total", value: result.total, color: "text-primary" },
      { label: "Open", value: result.open, color: "text-amber-700 dark:text-amber-400" },
      { label: "Known Errors", value: result.known_errors, color: "text-sky-700 dark:text-sky-400" },
      { label: "With Workaround", value: result.with_workaround, color: "text-emerald-700 dark:text-emerald-400" },
    ];
    return [
      { label: "Matches", value: result.total, color: "text-primary" },
      { label: "Open", value: result.open, color: "text-amber-700 dark:text-amber-400" },
      { label: "Resolved", value: result.resolved, color: "text-emerald-700 dark:text-emerald-400" },
      { label: "Breached", value: result.breached, color: result.breached ? "text-red-700 dark:text-red-400" : "" },
    ];
  };

  const columns = () => {
    if (dataset === "changes") return ["Number", "Title", "Status", "Priority", "Risk", "Created"];
    if (dataset === "problems") return ["Title", "Status", "Root cause", "Created"];
    return ["Number", "Title", "Status", "Priority", "Assignee", "Created", "Response", "Resolution"];
  };

  const rowCells = (r) => {
    const created = (r.created_at || "").slice(0, 10);
    if (dataset === "changes") return [r.number, r.title, (r.status || "").replace('_', ' '), r.priority, r.risk, created];
    if (dataset === "problems") return [r.title, (r.status || "").replace('_', ' '), r.root_cause || "—", created];
    return [r.number, r.title, (r.status || "").replace('_', ' '), r.priority, r.assignee_name || "Unassigned", created,
            hhmm(r.created_at, r.responded_at), hhmm(r.created_at, r.resolved_at)];
  };

  const exportCsv = () => {
    if (!result) return;
    const head = kpis().map(k => [k.label, k.value]);
    const lines = [
      [`${cfg.label} Report — grouped by`, result.grouped_by],
      ...head,
      [""],
      ["Bucket", "Count"],
      ...result.buckets.map(b => [b.key, b.count]),
      [""],
      columns(),
      ...result.rows.map(r => rowCells(r).map(c => `"${String(c ?? "").replace(/"/g, "'")}"`)),
    ];
    const csv = lines.map(l => l.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${dataset}-report-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status !== "all") params.set("status", filters.status);
      if (filters.priority !== "all") params.set("priority", filters.priority);
      if (filters.category !== "all") params.set("category", filters.category);
      if (filters.assignee_id !== "all") params.set("assignee_id", filters.assignee_id);
      if (filters.manager_id !== "all") params.set("manager_id", filters.manager_id);
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      const token = localStorage.getItem("helix_token");
      const res = await fetch(`${API}/incidents/export?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a"); a.href = url;
      a.download = `incidents-report-${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel exported");
    } catch { toast.error("Excel export failed"); }
  };

  return (
    <div className="p-6" data-testid="report-builder-view">
      <div className="flex items-end justify-between mb-6 print:hidden">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Custom Reporting</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black">Report Builder</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Build custom slices of Incidents, Changes and Problems, or view the per-client SLA &amp; billable-hours summary.</p>
        </div>
        {tab === "builder" && (
          <div className="flex gap-2">
            {dataset === "incidents" && <Button data-testid="rb-export-excel" onClick={exportExcel} variant="outline" className="rounded-sm font-mono text-xs uppercase tracking-widest"><Download size={14} className="mr-2" />Export Excel</Button>}
            <Button data-testid="rb-export-csv" onClick={exportCsv} variant="outline" className="rounded-sm font-mono text-xs uppercase tracking-widest"><FileCsv size={14} className="mr-2" />Export CSV</Button>
            <Button data-testid="pdf-export-btn" onClick={() => window.print()} variant="outline" className="rounded-sm font-mono text-xs uppercase tracking-widest"><Download size={14} className="mr-2" />Print</Button>
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-4 print:hidden" data-testid="report-tabs">
          <TabsTrigger value="builder" data-testid="tab-report-builder">Report Builder</TabsTrigger>
          <TabsTrigger value="client" data-testid="tab-client-report">Client Summary</TabsTrigger>
        </TabsList>

        <TabsContent value="builder">
      <div className="flex gap-2 mb-4 print:hidden" data-testid="rb-dataset-switch">
        {Object.entries(DATASETS).map(([k, v]) => (
          <Button key={k} data-testid={`rb-dataset-${k}`} onClick={() => switchDataset(k)} variant={dataset === k ? "default" : "outline"}
            className={`rounded-sm font-mono text-xs uppercase tracking-widest ${dataset === k ? "" : "border-border"}`}>{v.label}</Button>
        ))}
        {dataset === "incidents" && user && ["manager", "admin"].includes(user.role) && (
          <Button
            data-testid="rb-my-team-chip"
            onClick={() => { const next = { ...filters, manager_id: user.id }; setFilters(next); run(dataset, next); }}
            variant={filters.manager_id === user.id ? "default" : "outline"}
            className={`ml-auto rounded-sm font-mono text-xs uppercase tracking-widest ${filters.manager_id === user.id ? "" : "border-primary/40 text-primary"}`}>
            <UsersThree size={14} className="mr-2" />My team
          </Button>
        )}
      </div>

      <Card className="p-4 border-border rounded-sm mb-4 print:hidden" data-testid="rb-filters">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label className="text-[10px] font-mono uppercase text-muted-foreground">Status</Label><Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}><SelectTrigger data-testid="rb-status" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{cfg.statuses.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}</SelectContent></Select></div>
          {dataset === "incidents" && <div><Label className="text-[10px] font-mono uppercase text-muted-foreground">Priority</Label><Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v })}><SelectTrigger data-testid="rb-priority" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>}
          {dataset === "incidents" && <div><Label className="text-[10px] font-mono uppercase text-muted-foreground">Category</Label><Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}><SelectTrigger data-testid="rb-category" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>}
          {dataset === "incidents" && <div><Label className="text-[10px] font-mono uppercase text-muted-foreground">Assignee</Label><Select value={filters.assignee_id} onValueChange={(v) => setFilters({ ...filters, assignee_id: v })}><SelectTrigger data-testid="rb-assignee" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select></div>}
          {dataset === "incidents" && <div><Label className="text-[10px] font-mono uppercase text-muted-foreground">Manager (team)</Label><Select value={filters.manager_id} onValueChange={(v) => setFilters({ ...filters, manager_id: v })}><SelectTrigger data-testid="rb-manager" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{managers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent></Select></div>}
          <div><Label className="text-[10px] font-mono uppercase text-muted-foreground">Created from</Label><DatePicker testid="rb-date-from" value={filters.date_from} onChange={(v) => setFilters({ ...filters, date_from: v })} placeholder="Start date" /></div>
          <div><Label className="text-[10px] font-mono uppercase text-muted-foreground">Created to</Label><DatePicker testid="rb-date-to" value={filters.date_to} onChange={(v) => setFilters({ ...filters, date_to: v })} placeholder="End date" /></div>
          <div><Label className="text-[10px] font-mono uppercase text-muted-foreground">Group by</Label><Select value={filters.group_by} onValueChange={(v) => setFilters({ ...filters, group_by: v })}><SelectTrigger data-testid="rb-groupby" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger><SelectContent>{cfg.groupBys.map(g => <SelectItem key={g} value={g}>{g.replace('_', ' ')}</SelectItem>)}</SelectContent></Select></div>
          <div className="flex items-end gap-2">
            <Button data-testid="rb-run" onClick={() => run()} disabled={loading} className="rounded-sm font-mono uppercase text-xs h-10 flex-1">{loading ? "Running…" : "Run"}</Button>
            <Button data-testid="rb-reset" onClick={reset} variant="outline" className="rounded-sm font-mono uppercase text-xs h-10">Reset</Button>
          </div>
        </div>
      </Card>

      {result && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {kpis().map((k, i) => (
              <Card key={k.label} className="p-6 border-border rounded-sm" data-testid={i === 0 ? "rb-total" : `rb-kpi-${i}`}><div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">{k.label}</div><div className={`font-display text-4xl ${k.color}`}>{k.value}</div></Card>
            ))}
          </div>

          <Card className="p-6 border-border rounded-sm" data-testid="rb-chart">
            <div className="flex items-center gap-2 mb-3"><ChartBar size={16} className="text-primary" /><span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">{cfg.label} grouped by <span className="text-primary">{(result.grouped_by || "").replace('_', ' ')}</span></span></div>
            {result.buckets.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={result.buckets}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                  <XAxis dataKey="key" stroke={chart.axis} tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
                  <YAxis stroke={chart.axis} tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={chart.tooltip} />
                  <Bar dataKey="count" fill="hsl(48 100% 51%)" />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="text-sm text-muted-foreground py-8 text-center">No matching records.</div>}
          </Card>

          {result.rows.length > 0 && (
            <Card className="border-border rounded-sm overflow-hidden" data-testid="rb-rows">
              <div className="p-4 border-b border-border flex items-center justify-between gap-3 flex-wrap"><div className="font-display text-lg">Matching {cfg.label} <span className="text-sm text-muted-foreground font-mono">({result.rows.length} shown)</span></div>
                {dataset === "incidents" && (
                  <div className="flex items-center gap-1.5 text-[11px] font-mono text-muted-foreground" data-testid="rb-sla-legend">
                    <span className="inline-block w-2.5 h-2.5 rounded-sm bg-red-600 dark:bg-red-400" />
                    <span>Red = SLA breached (response / resolution)</span>
                  </div>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono border-b border-border">
                    {columns().map(c => <th key={c} className="text-left px-4 py-3">{c}</th>)}
                  </tr></thead>
                  <tbody>{result.rows.map((r, ri) => (
                    <tr key={r.id || ri} className="border-b border-border/60 hover:bg-muted/20">
                      {rowCells(r).map((cell, ci) => (
                        <td key={ci} className={`px-4 py-3 ${ci === 0 ? "font-mono text-primary" : "text-muted-foreground"} ${ci === 1 ? "max-w-xs truncate text-foreground" : ""} ${dataset === "incidents" && ci === 6 ? `font-mono ${slaBreachClass(r, "resp")}` : ""} ${dataset === "incidents" && ci === 7 ? `font-mono ${slaBreachClass(r, "resl")}` : ""}`}>
                          {(dataset !== "problems" && ci === 3) ? <span className={`text-xs font-mono px-1.5 py-0.5 border ${priorityColor(cell)}`}>{cell}</span> : cell}
                        </td>
                      ))}
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}
        </TabsContent>

        <TabsContent value="client">
          <ClientReport embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
}
